import React, { useEffect, useState } from 'react';
import {
  WttpQuestionListFilterInput,
  WttpQuestionnaireQuestionDto,
} from 'src/graphql/generated/graphql';
import { WttpQuestionnaireQuestionsFilter } from './filter';
import { useTable } from 'src/hooks/use-table';
import { Table } from '@otp/ui';
import { t } from 'i18next';
import { getTableConfig } from './table-config';
import styles from './index.module.css';
import { useQuestionnaireQuestions } from 'src/hooks/query/use-questionnaire-questions';

const PAGINATION_LIMIT = 10;
interface WttpQuestionsListFormProps {
  onChange: (selectedQuestions: WttpQuestionnaireQuestionDto[]) => void;
  selectedItems: WttpQuestionnaireQuestionDto[];
  wttpQuestionnaireId: string;
  loadingEntity: boolean;
}

export const WttpQuestionsListForm: React.FC<WttpQuestionsListFormProps> = ({
  onChange,
  selectedItems,
  wttpQuestionnaireId,
  loadingEntity,
}) => {
  const { page, setPage, filter, onFilterChange } = useTable<
    WttpQuestionListFilterInput,
    Record<any, any>
  >();
  const [selectedRows, setSelectedRows] =
    useState<WttpQuestionnaireQuestionDto[]>(selectedItems);
  useEffect(() => {
    setSelectedRows(selectedItems);
  }, [selectedItems, setSelectedRows]);
  const { loading: loadingQuestionsData, data: questionsData } =
    useQuestionnaireQuestions({
      filter: {
        wttpQuestionnaireId: wttpQuestionnaireId,
        question: filter,
      },
      page,
      paginationLimit: PAGINATION_LIMIT,
      shouldSkip: loadingEntity,
    });
  const wttpQuestions =
    questionsData?.wttpAllQuestionnaireQuestionsByQuestionnaireId?.records ||
    [];
  const totalCount =
    questionsData?.wttpAllQuestionnaireQuestionsByQuestionnaireId?.pagination
      .totalCount || 0;

  const tableConfig = getTableConfig(
    wttpQuestions || [],
    { t },
    page,
    PAGINATION_LIMIT,
  );

  const onRowSelected = (ids: string[]) => {
    const selectedQuestions = wttpQuestions
      .filter((question) => ids.some((id) => id == question.id))
      .map((item) => {
        return {
          id: item.id,
          order: item.order,
        };
      });
    let lastQuestionOrder = (page + 1) * PAGINATION_LIMIT;
    selectedQuestions.forEach((question) => {
      question.order = ++lastQuestionOrder;
    });
    const remainingQuestions = selectedRows
      .filter(
        (item) => !wttpQuestions.some((question) => question.id == item.id),
      )
      .map((item) => {
        return {
          id: item.id,
          order: item.order,
        };
      });
    const combinedQuestions = [...selectedQuestions, ...remainingQuestions];
    setSelectedRows(combinedQuestions);
    onChange(combinedQuestions);
  };

  const setRowProps = (
    row: any[],
    dataIndex: number,
    rowIndex: number,
  ): object => {
    if (!wttpQuestions[rowIndex].parentId) {
      return {};
    }
    return {
      style: {
        backgroundColor: 'var(--mui-palette-secondary-200)',
      },
    };
  };

  return (
    <div className="bg-white flex-1 flex flex-col">
      <WttpQuestionnaireQuestionsFilter
        onSubmit={onFilterChange}
        className={styles.filters}
      />
      <Table
        options={{
          count: totalCount,
          selectableRowsOnClick: true,
          rowsPerPage: PAGINATION_LIMIT,
          onChangePage: setPage,
          page: page,
          setRowProps: setRowProps,
        }}
        selectableRows="multiple"
        onRowsSelected={onRowSelected}
        data={wttpQuestions}
        columns={tableConfig}
        rowVariant="outlined"
        selectedRowIds={selectedItems.map((row) => row.id)}
        loading={loadingQuestionsData && loadingEntity}
      />
    </div>
  );
};
